import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react"

/** A context-appropriate "create" shortcut shown in a widget's menu (e.g. Budget Health → Create budget). */
export interface WidgetCreateAction {
  key: string
  label: string
  icon?: ReactNode
  onSelect: () => void
}

/**
 * Bridges a widget's *body* (the `render()` output, which owns the data query) and the widget *shell*
 * (`WidgetContainer`, which owns the three-dot menu and header chrome). The body registers which menu
 * actions it supports and whether it is quietly refetching; the shell reads that to render selectively.
 *
 * This keeps the generic engine's DnD/layout untouched: the shell no longer hardcodes four inert menu
 * items — each real widget declares exactly what it exposes. "Hide from dashboard" stays engine-owned
 * (driven by the container's own onRemove/onHide) and is not part of this contract.
 */
export interface WidgetMenuActions {
  /** Presence => a "Configure" item is shown; opens the widget's own configuration surface. */
  configure?: () => void
  /** Presence => a "Refresh" item is shown; refetches only this widget. */
  refresh?: () => void
  /** Presence => a "View details" item is shown; navigates to the widget's detail destination. */
  viewDetails?: () => void
  /** Context-appropriate create shortcuts (e.g. Record income, Create budget), shown as their own group. */
  create?: WidgetCreateAction[]
}

interface WidgetChromeContextValue {
  actions: WidgetMenuActions
  setActions: (actions: WidgetMenuActions) => void
  isRefreshing: boolean
  setRefreshing: (value: boolean) => void
}

const WidgetChromeContext = createContext<WidgetChromeContextValue | null>(null)

/** Wraps a single widget (shell + body) so the body can register menu actions the shell renders. */
export function WidgetChromeProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<WidgetMenuActions>({})
  const [isRefreshing, setRefreshing] = useState(false)
  const value = useMemo<WidgetChromeContextValue>(
    () => ({ actions, setActions, isRefreshing, setRefreshing }),
    [actions, isRefreshing],
  )
  return <WidgetChromeContext.Provider value={value}>{children}</WidgetChromeContext.Provider>
}

/** Shell side: the currently registered menu actions + refreshing flag. Safe outside a provider. */
export function useWidgetChromeState(): { actions: WidgetMenuActions; isRefreshing: boolean } {
  const ctx = useContext(WidgetChromeContext)
  return { actions: ctx?.actions ?? {}, isRefreshing: ctx?.isRefreshing ?? false }
}

/**
 * Body side: declare this widget's menu actions. Presence of a handler => that item appears. The effect
 * only re-runs when the *set* of available actions changes (not on every closure identity), so passing
 * inline handlers is fine — the latest closure is always invoked via a ref.
 */
export function useWidgetMenu(actions: WidgetMenuActions): void {
  const ctx = useContext(WidgetChromeContext)
  const setActions = ctx?.setActions
  const latest = useRef(actions)
  latest.current = actions

  const hasConfigure = Boolean(actions.configure)
  const hasRefresh = Boolean(actions.refresh)
  const hasViewDetails = Boolean(actions.viewDetails)
  // Signature of the create group (keys + labels) — the effect re-runs only when this changes.
  const createSignature = (actions.create ?? []).map((item) => `${item.key}:${item.label}`).join("|")

  useEffect(() => {
    if (!setActions) return
    const create = (latest.current.create ?? []).map((item) => ({
      key: item.key,
      label: item.label,
      icon: item.icon,
      onSelect: () => latest.current.create?.find((candidate) => candidate.key === item.key)?.onSelect(),
    }))
    setActions({
      configure: hasConfigure ? () => latest.current.configure?.() : undefined,
      refresh: hasRefresh ? () => latest.current.refresh?.() : undefined,
      viewDetails: hasViewDetails ? () => latest.current.viewDetails?.() : undefined,
      create: create.length > 0 ? create : undefined,
    })
    return () => setActions({})
  }, [setActions, hasConfigure, hasRefresh, hasViewDetails, createSignature])
}

/**
 * Body side: report a subtle "refreshing" state (a background refetch while loaded data is still shown).
 * The shell renders a small spinner rather than swapping content for a full skeleton.
 */
export function useWidgetRefreshing(isRefreshing: boolean): void {
  const ctx = useContext(WidgetChromeContext)
  const setRefreshing = ctx?.setRefreshing
  useEffect(() => {
    setRefreshing?.(isRefreshing)
    return () => setRefreshing?.(false)
  }, [setRefreshing, isRefreshing])
}
